import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, Rocket, ChevronDown } from "lucide-react";
import { trackDemoClick, trackTrialClick } from "@/lib/analytics";
import logo from "@/assets/complyhub-logo.svg";
import TopBar from "@/components/TopBar";
import { useTopBarVisibility } from "@/hooks/useTopBarVisibility";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const productLinks = [
  { href: "/product", label: "Product Overview" },
  { href: "/features", label: "All Features" },
  { href: "/rto-compliance-software", label: "RTO Compliance Software" },
  { href: "/features/governance-meeting-manager", label: "Governance Meeting Manager" },
  { href: "/features/documents-register-repository", label: "Documents Register" },
  { href: "/registers", label: "Compliance Registers" },
  { href: "/features/trainers-matrix", label: "Trainers Matrix" },
  { href: "/features/tas-builder", label: "TAS Quality Engine" },
  { href: "/features/industry-consultation", label: "Industry Consultation" },
  { href: "/features/complybot", label: "ComplyBot" },
  { href: "/sms", label: "ComplyHub SMS — Coming Soon" },
];

const compareLinks = [
  { href: "/vs/rtosafe", label: "ComplyHub vs RTOSafe" },
  { href: "/vs/octossure", label: "ComplyHub vs Octossure" },
  { href: "/vs/eskilled", label: "ComplyHub vs eSkilled" },
];

const resourceLinks = [
  { href: "/resources", label: "Resources Hub" },
  { href: "/demo-library", label: "Demo Library" },
  { href: "/blog", label: "Blog" },
  { href: "/compliance-scorecard", label: "Compliance Scorecard" },
  { href: "/faq", label: "FAQ" },
  { href: "/podcast", label: "RTO Superhero Podcast" },
];

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const topBarVisible = useTopBarVisibility();

  const isProductActive = location.pathname === "/product" || location.pathname.startsWith("/features");
  const isCompareActive = location.pathname.startsWith("/vs");
  const isResourcesActive = location.pathname === "/resources" || location.pathname === "/demo-library" || location.pathname === "/blog" || location.pathname.startsWith("/blog/") || location.pathname === "/faq" || location.pathname.startsWith("/compliance-scorecard/start") || location.pathname === "/podcast";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Single Book-a-Demo variant, computed from scroll state — no visibility toggling.
  const bookDemoVariant = scrolled ? "pill-ghost-dark" : "outline";

  return (
    <>
      <TopBar isVisible={topBarVisible} />
      <header
        data-fixed-header="true"
        className={`fixed left-0 right-0 z-[61] transition-all duration-300 ${
          scrolled
            ? "bg-white shadow-[0_2px_8px_rgba(0,0,0,0.06)] border-b border-transparent"
            : "bg-white/90 backdrop-blur-sm border-b border-ch-acai/10"
        }`}
        style={{ top: topBarVisible ? "calc(var(--banner-offset, 0px) + 2.25rem)" : "var(--banner-offset, 0px)" }}
      >
        <div className="container flex items-center justify-between h-16 md:h-20">
        <Link to="/" className="flex items-center">
          <img 
            src={logo} 
            alt="ComplyHub — RTO compliance management software"
            className="h-10 md:h-12 w-auto" 
            width={125}
            height={50}
            fetchPriority="high"
          />
        </Link>
        
        <nav className="hidden md:flex items-center gap-6 lg:gap-8">
          {/* Product Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger data-active={isProductActive} className={`nav-underline flex items-center gap-1 font-medium transition-colors ${
              isProductActive 
                ? "text-ch-purple" 
                : "text-ch-acai"
            }`}>
              Product
              <ChevronDown className="w-4 h-4" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="bg-white border-ch-acai/10">
              {productLinks.map((link) => (
                <DropdownMenuItem key={link.href} asChild>
                  <Link 
                    to={link.href}
                    className={`w-full cursor-pointer ${
                      location.pathname === link.href ? "text-ch-purple" : "text-ch-acai hover:text-ch-fuchsia"
                    }`}
                  >
                    {link.label}
                  </Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <Link 
            to="/compliance" 
            data-active={location.pathname === "/compliance"}
            className={`nav-underline font-medium transition-colors ${
              location.pathname === "/compliance" 
                ? "text-ch-purple" 
                : "text-ch-acai"
            }`}
          >
            Standards Alignment
          </Link>

          <Link 
            to="/pricing" 
            data-active={location.pathname === "/pricing"}
            className={`nav-underline font-medium transition-colors ${
              location.pathname === "/pricing" 
                ? "text-ch-purple" 
                : "text-ch-acai"
            }`}
          >
            Pricing
          </Link>

          {/* Compare Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger data-active={isCompareActive} className={`nav-underline flex items-center gap-1 font-medium transition-colors ${
              isCompareActive 
                ? "text-ch-purple" 
                : "text-ch-acai"
            }`}>
              Compare
              <ChevronDown className="w-4 h-4" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="bg-white border-ch-acai/10">
              {compareLinks.map((link) => (
                <DropdownMenuItem key={link.href} asChild>
                  <Link 
                    to={link.href}
                    className={`w-full cursor-pointer ${
                      location.pathname === link.href ? "text-ch-purple" : "text-ch-acai hover:text-ch-fuchsia"
                    }`}
                  >
                    {link.label}
                  </Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Resources Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger data-active={isResourcesActive} className={`nav-underline flex items-center gap-1 font-medium transition-colors ${
              isResourcesActive 
                ? "text-ch-purple" 
                : "text-ch-acai"
            }`}>
              Resources
              <ChevronDown className="w-4 h-4" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="bg-white border-ch-acai/10">
              {resourceLinks.map((link) => (
                <DropdownMenuItem key={link.href} asChild>
                  <Link 
                    to={link.href}
                    className={`w-full cursor-pointer ${
                      location.pathname === link.href ? "text-ch-purple" : "text-ch-acai hover:text-ch-fuchsia"
                    }`}
                  >
                    {link.label}
                  </Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </nav>

        <div className="flex items-center gap-3">
          <Button 
            size="sm"
            variant="pill-primary"
            className="hidden sm:inline-flex"
            asChild
          >
            <a 
              href="/trial-signup"
              onClick={() => trackTrialClick('header')}
              aria-label="Start your free ComplyHub trial"
            >
              Start Free Trial
            </a>
          </Button>
          <Button
            size="sm"
            variant={bookDemoVariant}
            className="hidden md:inline-flex"
            asChild
          >
            <Link
              to="/demo#book"
              onClick={() => trackDemoClick('header')}
              aria-label="Book a live demo of ComplyHub"
            >
              Book a Demo
            </Link>
          </Button>
          
          {/* Mobile menu button */}
          <button 
            className="md:hidden p-2 text-ch-acai"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? "Close navigation menu" : "Open navigation menu"}
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-[#7130A0] border-b border-white/10 text-white max-h-[calc(100dvh-7.5rem)] overflow-y-auto overscroll-contain">
          <nav className="container py-4 flex flex-col gap-4">
            {/* Product section */}
            <div className="flex flex-col">
              <span className="font-medium text-white py-2">Product</span>
              <div className="pl-4 flex flex-col gap-2">
                {productLinks.map((link) => (
                  <Link 
                    key={link.href}
                    to={link.href} 
                    className={`text-sm py-1 transition-colors ${
                      location.pathname === link.href 
                        ? "text-[#F9CB0C]"
                        : "text-white/85 hover:text-[#F9CB0C]"
                    }`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>

            <Link 
              to="/compliance" 
              className={`font-medium py-2 transition-colors ${
                location.pathname === "/compliance" 
                  ? "text-[#F9CB0C]"
                  : "text-white hover:text-[#F9CB0C]"
              }`}
              onClick={() => setMobileMenuOpen(false)}
            >
              Standards Alignment
            </Link>

            <Link 
              to="/pricing" 
              className={`font-medium py-2 transition-colors ${
                location.pathname === "/pricing" 
                  ? "text-[#F9CB0C]"
                  : "text-white hover:text-[#F9CB0C]"
              }`}
              onClick={() => setMobileMenuOpen(false)}
            >
              Pricing
            </Link>

            {/* Compare section */}
            <div className="flex flex-col">
              <span className="font-medium text-white py-2">Compare</span>
              <div className="pl-4 flex flex-col gap-2">
                {compareLinks.map((link) => (
                  <Link 
                    key={link.href}
                    to={link.href} 
                    className={`text-sm py-1 transition-colors ${
                      location.pathname === link.href 
                        ? "text-[#F9CB0C]"
                        : "text-white/85 hover:text-[#F9CB0C]"
                    }`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>

            {/* Resources section */}
            <div className="flex flex-col">
              <span className="font-medium text-white py-2">Resources</span>
              <div className="pl-4 flex flex-col gap-2">
                {resourceLinks.map((link) => (
                  <Link 
                    key={link.href}
                    to={link.href} 
                    className={`text-sm py-1 transition-colors ${
                      location.pathname === link.href 
                        ? "text-[#F9CB0C]"
                        : "text-white/85 hover:text-[#F9CB0C]"
                    }`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>

            <div className="flex flex-col gap-2 pt-4 border-t border-white/15">
              <Button variant="pill-primary" asChild className="w-full">
                <a
                  href="/trial-signup"
                  onClick={() => {
                    trackTrialClick('header_mobile');
                    setMobileMenuOpen(false);
                  }}
                  aria-label="Start your free ComplyHub trial"
                >
                  <Rocket className="w-4 h-4" />
                  Start Free Trial
                </a>
              </Button>
              <Button variant="pill-ghost" asChild className="w-full">
                <Link
                  to="/demo#book"
                  onClick={() => {
                    trackDemoClick('header_mobile');
                    setMobileMenuOpen(false);
                  }}
                  aria-label="Book a live demo of ComplyHub"
                >
                  Book a Demo
                </Link>
              </Button>
            </div>
          </nav>
        </div>
      )}
      </header>
    </>
  );
};

export default Header;
